<app-header-nav></app-header-nav>

<div class="container mx-auto p-6 max-w-2xl">

  <!-- ── One notification in full ─────────────────────────────────────── -->
  <ng-container *ngIf="selected as n">
    <button mat-button (click)="closeDetail()" class="mb-4" data-test="detail-back">
      <mat-icon>arrow_back</mat-icon> Back to notifications
    </button>
    <mat-card class="p-6" data-test="detail">
      <div class="flex items-center gap-3 mb-4">
        <span class="flex items-center justify-center rounded-full"
              [style.background]="n.kind.color + '26'" style="width:52px;height:52px">
          <mat-icon [style.color]="n.kind.color">{{ n.kind.icon }}</mat-icon>
        </span>
        <span class="text-sm font-semibold rounded-full px-3 py-1"
              [style.color]="n.kind.color" [style.background]="n.kind.color + '1F'">
          {{ n.kind.label }}
        </span>
      </div>
      <h2 class="text-2xl font-bold" data-test="detail-title">{{ n.title }}</h2>
      <div *ngIf="n.sentAt" class="flex items-center gap-1 text-gray-600 mt-1">
        <mat-icon style="font-size:18px;width:18px;height:18px">schedule</mat-icon>
        {{ n.sentAt | date:'EEEE d MMMM y, HH:mm' }}
      </div>
      <hr class="my-5">
      <p class="text-base leading-relaxed whitespace-pre-line" data-test="detail-message">{{ n.message }}</p>
      <div class="flex items-center gap-1 mt-5 text-green-700">
        <mat-icon style="font-size:18px;width:18px;height:18px">done_all</mat-icon> Read
      </div>
    </mat-card>
  </ng-container>

  <!-- ── The list ─────────────────────────────────────────────────────── -->
  <ng-container *ngIf="!selected">
    <div class="flex items-center justify-between mb-2">
      <div class="flex items-center">
        <button mat-icon-button (click)="onBack()" aria-label="Back"><mat-icon>arrow_back</mat-icon></button>
        <h1 class="text-2xl font-bold ml-2">Notifications</h1>
      </div>
      <div class="flex items-center">
        <button mat-icon-button (click)="load()" aria-label="Refresh" title="Refresh">
          <mat-icon>refresh</mat-icon>
        </button>
        <button mat-button color="primary" (click)="markAllAsRead()" [disabled]="unreadCount === 0"
                data-test="mark-all-read">
          <mat-icon>done_all</mat-icon> Mark all read
        </button>
      </div>
    </div>

    <div class="flex items-center justify-between mb-4" *ngIf="!isLoading && !error && notifications.length">
      <span class="text-lg font-bold" data-test="unread-summary">
        {{ unreadCount === 0 ? 'All caught up' : unreadCount + ' unread' }}
      </span>
      <mat-button-toggle-group [value]="filter" (change)="filter = $event.value" hideSingleSelectionIndicator>
        <mat-button-toggle value="all" data-test="filter-all">All</mat-button-toggle>
        <mat-button-toggle value="unread" data-test="filter-unread">
          Unread{{ unreadCount ? ' (' + unreadCount + ')' : '' }}
        </mat-button-toggle>
      </mat-button-toggle-group>
    </div>

    <div *ngIf="isLoading" class="flex justify-center py-12">
      <mat-spinner></mat-spinner>
    </div>

    <p *ngIf="error" class="text-center text-red-600 py-12">
      <mat-icon style="font-size:48px;width:48px;height:48px">error_outline</mat-icon><br>{{ error }}
    </p>

    <ng-container *ngIf="!isLoading && !error">
      <div *ngFor="let n of visible"
           class="mb-3 rounded-xl cursor-pointer overflow-hidden flex"
           [style.background]="tint(n)"
           [style.box-shadow]="n.isRead ? '0 1px 2px rgba(0,0,0,.08)' : '0 2px 6px rgba(0,0,0,.15)'"
           [attr.data-test]="'notification-' + n.id"
           [attr.aria-label]="(n.isRead ? 'Read ' : 'Unread ') + n.kind.label"
           role="button" tabindex="0"
           (click)="open(n)" (keydown.enter)="open(n)">
        <!-- Accent bar: present only while unread. -->
        <div style="width:4px;flex:none" [style.background]="n.isRead ? 'transparent' : n.kind.color"></div>
        <div class="flex items-start gap-3 p-4 flex-1 min-w-0">
          <span class="flex items-center justify-center rounded-full flex-none"
                [style.background]="n.kind.color + (n.isRead ? '14' : '26')" style="width:40px;height:40px">
            <mat-icon [style.color]="n.kind.color" [style.opacity]="n.isRead ? 0.6 : 1">{{ n.kind.icon }}</mat-icon>
          </span>
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2">
              <span class="flex-1 truncate"
                    [class.font-bold]="!n.isRead" [class.font-medium]="n.isRead"
                    [class.text-gray-900]="!n.isRead" [class.text-gray-600]="n.isRead">{{ n.title }}</span>
              <span class="text-xs whitespace-nowrap"
                    [style.color]="n.isRead ? '#9e9e9e' : n.kind.color"
                    [class.font-semibold]="!n.isRead">{{ relativeTime(n.sentAt) }}</span>
            </div>
            <div class="text-sm mt-1 line-clamp-2"
                 [class.text-gray-900]="!n.isRead" [class.text-gray-500]="n.isRead">{{ n.message }}</div>
            <div class="text-xs font-semibold tracking-wide mt-1 uppercase"
                 [style.color]="n.kind.color" [style.opacity]="n.isRead ? 0.6 : 1">{{ n.kind.label }}</div>
          </div>
          <span *ngIf="!n.isRead" class="rounded-full flex-none mt-2"
                [attr.data-test]="'unread-dot-' + n.id"
                style="width:10px;height:10px" [style.background]="n.kind.color"></span>
          <mat-icon *ngIf="n.isRead" class="text-gray-400 flex-none">chevron_right</mat-icon>
        </div>
      </div>

      <p *ngIf="filter === 'unread' && visible.length === 0 && notifications.length"
         class="text-center text-gray-500 py-12">No unread notifications</p>

      <div class="flex justify-center my-4" *ngIf="hasMore && filter === 'all' && notifications.length">
        <button mat-stroked-button (click)="loadMore()" [disabled]="loadingMore" data-test="load-more">
          {{ loadingMore ? 'Loading…' : 'Load older notifications' }}
        </button>
      </div>

      <p *ngIf="notifications.length === 0" class="text-center text-gray-500 py-12">
        <mat-icon class="text-gray-300" style="font-size:48px;width:48px;height:48px">notifications_none</mat-icon>
        <br>Nothing yet. Orders, payments and stock alerts show up here.
      </p>
    </ng-container>
  </ng-container>
</div>

<app-footer></app-footer>
